import { useEffect, useState } from "react";
import axios from "axios";
import { Handshake, KeyRound, ShieldCheck, Users, Crown, ChevronRight } from "lucide-react";
import { BUYER_BADGES } from "../lib/badges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPONSOR_ICON = { introducer: Handshake, custodian: KeyRound, guarantor: ShieldCheck };

const StatusDot = ({ status }) => {
  const c = status === "active" ? "#2FB999" : status === "pending_kyc" || status === "awaiting_approval" ? "#B87333" : "#8A8A82";
  return <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: c }} title={status} />;
};

const SubRoleChip = ({ sub_role }) => {
  const label = sub_role === "independent" ? "3A · IND"
    : sub_role === "introducer_dependent" ? "3B · INTRO-DEP"
    : sub_role === "guarantor_dependent" ? "3C · GUAR-DEP"
    : sub_role === "custodian_dependent" ? "3D · CUST-DEP"
    : sub_role;
  return (
    <span className="font-grotesk text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-sm border border-white/15 text-[#C9C9C2] bg-white/[0.03]">
      {label}
    </span>
  );
};

/**
 * Admin-only hierarchy tree.  Grouped view of every Level-2 sponsor and their
 * dependent Super-Stockists, plus a separate bucket for Independent (3A) or
 * unlinked SS partners.  Renders on the /admin overview.
 */
export const AdminHierarchyTree = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/hierarchy`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const toggle = (id) => setCollapsed((s) => ({ ...s, [id]: !s[id] }));

  const total = (data.sponsors || []).reduce((n, s) => n + (s.dependents?.length || 0), 0) + (data.independents?.length || 0);

  return (
    <div data-testid="admin-hierarchy-tree" className="border border-white/10 rounded-sm bg-[#0a0a0a] p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Users size={16} className="text-[#B87333]" />
            <h3 className="font-serif text-2xl">Org hierarchy</h3>
          </div>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            {data.sponsors.length} Level-2 sponsor{data.sponsors.length === 1 ? "" : "s"} · {total} Super-Stockist{total === 1 ? "" : "s"} total
          </p>
        </div>
      </div>

      {data.sponsors.length === 0 && data.independents.length === 0 && (
        <div className="p-6 font-body text-xs text-[#8A8A82]">No partners yet.</div>
      )}

      {/* Sponsor tree */}
      <div className="space-y-3">
        {data.sponsors.map((s) => {
          const Icon = SPONSOR_ICON[s.category] || Crown;
          const b = BUYER_BADGES[s.category];
          const open = !collapsed[s.id];
          return (
            <div key={s.id} data-testid={`hierarchy-sponsor-${s.id}`} className="border border-white/[0.08] rounded-sm overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(s.id)}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/[0.02] transition-colors"
              >
                <ChevronRight size={14} className={`text-[#8A8A82] shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
                <span
                  className="grid place-items-center w-8 h-8 rounded-sm shrink-0 text-black"
                  style={{ background: `linear-gradient(135deg, ${b?.from || "#333"}, ${b?.to || "#666"})` }}
                >
                  <Icon size={14} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-base text-[#F5F5F0] truncate">{s.business_name}</span>
                    <span className="font-grotesk text-[10px] tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-[#B87333]/45 text-[#B87333] bg-[#B87333]/[0.06]">{s.vendor_code}</span>
                    <StatusDot status={s.status} />
                  </span>
                  <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] block mt-0.5">
                    {b?.label || s.category} · {s.state || "—"} · {s.dependents.length} dependent{s.dependents.length === 1 ? "" : "s"}
                  </span>
                </span>
              </button>
              {open && s.dependents.length > 0 && (
                <ul className="border-t border-white/[0.06] bg-black/40">
                  {s.dependents.map((d) => (
                    <li key={d.id} data-testid={`hierarchy-dep-${d.id}`} className="pl-10 pr-4 py-2 flex items-center gap-3 border-b border-white/[0.04] last:border-b-0">
                      <span className="inline-block w-px h-4 bg-white/10" />
                      <span className="text-[#8A8A82]">—</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="font-body text-sm text-[#F5F5F0] truncate">{d.business_name}</span>
                          <span className="font-grotesk text-[9px] tracking-[0.16em] px-1.5 py-0.5 rounded-sm border border-[#B87333]/40 text-[#B87333] bg-[#B87333]/[0.06]">{d.vendor_code}</span>
                          <SubRoleChip sub_role={d.sub_role} />
                          <StatusDot status={d.status} />
                        </span>
                        <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#6f6f68] block mt-0.5">{d.state || "—"}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {open && s.dependents.length === 0 && (
                <div className="pl-10 pr-4 py-3 font-body text-xs text-[#8A8A82] border-t border-white/[0.06] bg-black/30">
                  No dependent Super-Stockists yet.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Independent SS bucket */}
      {data.independents.length > 0 && (
        <div className="mt-5 border border-dashed border-white/10 rounded-sm p-4" data-testid="hierarchy-independents">
          <div className="flex items-center gap-2 mb-2">
            <Crown size={13} className="text-[#8A8A82]" />
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
              3A · Independent Super-Stockists ({data.independents.length})
            </span>
          </div>
          <ul className="grid md:grid-cols-2 gap-x-6 gap-y-1.5">
            {data.independents.slice(0, 20).map((o) => (
              <li key={o.id} className="flex items-center gap-2 min-w-0">
                <StatusDot status={o.status} />
                <span className="font-body text-xs text-[#C9C9C2] truncate">{o.business_name}</span>
                <span className="font-grotesk text-[9px] tracking-[0.16em] px-1.5 py-0.5 rounded-sm border border-[#B87333]/40 text-[#B87333] bg-[#B87333]/[0.06] shrink-0">{o.vendor_code}</span>
              </li>
            ))}
          </ul>
          {data.independents.length > 20 && (
            <div className="font-body text-[10px] text-[#8A8A82] mt-2">+ {data.independents.length - 20} more…</div>
          )}
        </div>
      )}
    </div>
  );
};
